import { Link } from 'react-router-dom';

import { formatearFecha } from '../../../nucleo/fecha/fechaLocal.js';
import {
  claseDeColorDelImporte, importe as formatearImporte, presentacionDelImporte, tituloDelMovimiento,
} from '../../movimientos/formato.js';
import { insigniaDelTipo } from '../logica/agregacion.js';
import {
  ESTADO_DE_TARJETA, leerRenglonesRecientes, resolverEstadoDeTarjeta,
} from '../logica/estadosHonestos.js';
import { TarjetaCargando } from './EstadoDeTarjeta.jsx';

// RF-215, RN-614 · misma hoja de color por tipo que el historial.
import '../../../estilos/importes-por-tipo.css';

/*
 * T-4 · ÚLTIMOS MOVIMIENTOS (RF-214, D-316, RN-609, RN-610).
 *
 * TRES RENGLONES, TODOS LOS TIPOS (D-316): gasto, ingreso, traspaso y devolución.
 * Son los primeros renglones de la MISMA consulta que alimenta el resumen del mes,
 * en el orden del servidor y SIN filtros añadidos — de modo que un renglón de aquí
 * es EL MISMO que encabeza el historial al abrir Movimientos. Si difieren en
 * contenido u orden, es defecto (RN-600, CA-14-11).
 *
 * En consecuencia, y sin que esta tarjeta decida nada: los cancelados quedan fuera
 * (el Tablero no ofrece «ver cancelados»), los eliminados no existen, y las
 * mensualidades de compras a meses aparecen como renglón repartido con su
 * mensualidad y su fecha de corte, igual que en el historial (D-69).
 *
 * RN-610 · MES SIN RENGLONES: LA TARJETA NO SE DIBUJA y no deja hueco, porque el
 * resumen del mes ya informa el mes vacío con su mensaje y su acceso a capturar.
 * Dos tarjetas diciendo «no hay nada» es una de más.
 *
 * El importe lleva su signo y su color por tipo con la MISMA regla del historial
 * (RF-215, RN-614), tomada de `formato.js` del 3/6 para no tener dos criterios.
 */
export function TarjetaDeUltimosMovimientos({ nombreDelMes, movimientos }) {
  const TITULO = 'Últimos movimientos';

  /* FIX del 2026-10-08 · misma regla general que las demás (RN-622). Aquí los
     cuatro estados se reparten distinto porque RN-610 ya lo decidía así:
       · ERROR  → no se dibuja. Su fuente es la misma del resumen del mes, que ya
                  avisa del fallo con su «Reintentar»; un segundo aviso del mismo
                  error sería ruido (RNF-112). Lo que cambia con el FIX es que una
                  respuesta ilegible cae AQUÍ y no en «no hay movimientos».
       · VACIO  → no se dibuja (RN-610): el resumen del mes ya informa el mes
                  vacío con su mensaje y su acceso a capturar.
     La diferencia entre los dos la sigue contando el resumen del mes, que es
     quien tiene el texto; esta tarjeta solo desaparece. */
  const recientes = resolverEstadoDeTarjeta(movimientos, leerRenglonesRecientes);

  if (recientes.estado === ESTADO_DE_TARJETA.ERROR) return null;

  if (recientes.estado === ESTADO_DE_TARJETA.CARGANDO) {
    return <TarjetaCargando titulo={TITULO} renglones={3} dato="ultimos-movimientos-cargando" />;
  }

  if (recientes.estado === ESTADO_DE_TARJETA.VACIO) return null;

  const renglones = recientes.valor;

  return (
    <section aria-label={TITULO} data-prueba="ultimos-movimientos">
      <div className="gtit">{TITULO}</div>
      <div className="card lista">
        {renglones.map((movimiento) => {
          const presentacion = presentacionDelImporte(movimiento);
          const insignia = insigniaDelTipo(movimiento.tipo);
          const titulo = tituloDelMovimiento(movimiento);
          const concepto = movimiento.nota ? movimiento.nota : titulo;

          /* [AÑADIDO · incremento 19/21 · D-425, RN-736e] Esta tarjeta lee la MISMA
             respuesta del historial (ConsultarMovimientos), así que también le llegan
             los renglones imputados de un plan a meses EN CURSO, que NO TIENEN GASTO
             DETRÁS. Sin esta guarda el renglón quedaba envuelto en un enlace a
             `/movimientos/0` —una pantalla que no existe— y tocarlo llevaba a un
             error. Se dibuja igual, pero sin enlace: aquí tampoco se gana navegación.
             El texto del renglón ya lo resuelve `tituloDelMovimiento`. */
          const sinDetalle = movimiento.esPlanEnCurso === true;
          const Contenedor = sinDetalle ? 'div' : Link;
          const propsDelContenedor = sinDetalle
            ? { 'data-prueba': 'renglon-reciente', 'data-en-curso': 'true' }
            : {
                to: `/movimientos/${movimiento.idMovimiento}`,
                'data-prueba': 'renglon-reciente',
                'data-id': movimiento.idMovimiento,
              };

          return (
            <Contenedor
              className="fila"
              key={`${movimiento.idMovimiento}-${movimiento.contadorDeMensualidad ?? ''}`}
              {...propsDelContenedor}
            >
              <span
                className="punto"
                aria-hidden="true"
                style={{
                  background: insignia.fondo,
                  border: `1px solid ${insignia.borde}`,
                  color: insignia.texto,
                }}
              >
                {insignia.icono}
              </span>

              <span className="crece">
                <b>{concepto}</b>
                {/* «fecha · categoría · cuenta · persona» (RF-214). El nombre del
                    tipo va escrito para que el color no sea el único portador. */}
                <small>
                  {formatearFecha(movimiento.fecha)}
                  {' · '}{titulo}
                  {' · '}{movimiento.nombreCuenta}
                  {movimiento.nombreCuentaDestino && ` → ${movimiento.nombreCuentaDestino}`}
                  {' · '}{movimiento.nombrePropietario}
                </small>
                {movimiento.esRenglonRepartido && (
                  <small data-prueba="renglon-repartido-reciente">
                    Mensualidad {movimiento.contadorDeMensualidad} de una compra a meses
                  </small>
                )}
              </span>

              <span className={`imp ${claseDeColorDelImporte(movimiento)}`.trim()} data-prueba="importe-reciente">
                {presentacion.signo}{formatearImporte(movimiento.importe, movimiento.codigoISO)}
              </span>
            </Contenedor>
          );
        })}

        <div className="tpie">
          <span className="crece">Lo último que se capturó en {nombreDelMes}, de cualquier tipo.</span>
          {/* «Ver todos» abre el historial SIN filtros (RF-214, CA-14-11). */}
          <Link className="btn sec" to="/movimientos" data-prueba="ver-todos-los-movimientos">Ver todos</Link>
        </div>
      </div>
    </section>
  );
}
